import axios from 'axios';
import { toast } from 'react-toastify';

class IPDService {
    // Patient Management
    async getPatients(params = {}) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`/api/v1/ipd/patients`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'hgs',
                },
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async getPatientById(patientId) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`/api/v1/ipd/${patientId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'hgs',
                },
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async createAdmission(admissionData) {
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();

            // Append admission data
            Object.keys(admissionData).forEach(key => {
                if (key === 'documents') {
                    admissionData[key].forEach(file => {
                        formData.append('documents', file);
                    });
                } else {
                    formData.append(key, admissionData[key]);
                }
            });

            const response = await axios.post(
                `/api/v1/ipd/admissions`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async updatePatient(patientId, updateData) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.put(
                `/api/v1/ipd/patients/${patientId}`,
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Timeline Management
    async addTimelineEntry(ipdVisitId, entryData) {
        try {
            const token = localStorage.getItem('authToken');

            const response = await axios.post(
                `/api/v1/ipd/${ipdVisitId}/timeline`,
                entryData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to add timeline entry';
            this.handleError(error);
            throw new Error(errorMessage);
        }
    }

    async updateTimelineEntry(patientId, entryId, updateData) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.put(
                `/api/v1/ipd/patients/${patientId}/timeline/${entryId}`,
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async deleteTimelineEntry(patientId, entryId) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.delete(
                `/api/v1/ipd/patients/${patientId}/timeline/${entryId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Bed Management
    async getAvailableBeds(wardId) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `/api/v1/ipd/wards/${wardId}/beds`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async assignBed(patientId, bedData) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(
                `/api/v1/ipd/patients/${patientId}/bed-assignment`,
                bedData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Reports
    async getAdmissionReport(startDate, endDate) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `/api/v1/ipd/reports/admissions`,
                {
                    params: { startDate, endDate },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async getDischargeReport(startDate, endDate) {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `/api/v1/ipd/reports/discharges`,
                {
                    params: { startDate, endDate },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async getWardOccupancyReport() {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `/api/v1/ipd/reports/ward-occupancy`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'hgs',
                    },
                }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Error handling
    handleError(error) {
        if (error.response && error.response.data && error.response.data.message) {
            toast.error(error.response.data.message);
        } else {
            toast.error('An error occurred');
        }
    }
}

export default IPDService; 